// src/components/CopyProgramMapping.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiCopy, FiCheck, FiAlertCircle, FiLayers,
  FiArrowRight,  FiLoader, FiAlertTriangle, FiRefreshCw,
  FiChevronDown, FiGrid,  FiBook, FiPercent,
  FiChevronUp,  FiCheckCircle, 
    FiEye, FiBarChart2
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../api';
import useAcademicData from './hooks/useAcademicData';

// --- Internal Components ---

const StepIndicator = ({ currentStep, steps }) => {
  return (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isActive = currentStep === stepNumber;
        const isCompleted = currentStep > stepNumber;
        
        return (
          <div key={step} className="flex-1 relative">
            <div className="flex items-center">
              <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center ${
                isActive ? 'bg-indigo-600 text-white' :
                isCompleted ? 'bg-green-500 text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {isCompleted ? <FiCheck className="w-4 h-4" /> : stepNumber}
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-2 ${
                  isCompleted ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
            <span className={`absolute -bottom-6 left-0 text-xs font-medium whitespace-nowrap ${
              isActive ? 'text-indigo-600' : 'text-gray-500'
            }`}>
              {step}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const StatCard = ({ label, value, subtext, icon: Icon, trend, loading }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-start justify-between">
        <div className={`p-2.5 rounded-lg ${trend ? 'bg-opacity-10' : 'bg-gray-50'}`}>
          <Icon className={`w-5 h-5 ${trend ? 'text-indigo-600' : 'text-gray-600'}`} />
        </div>
        {trend && (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
            trend === 'up' ? 'bg-green-50 text-green-600' :
            trend === 'down' ? 'bg-red-50 text-red-600' :
            'bg-gray-50 text-gray-600'
          }`}>
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : ''} {trend}
          </span>
        )}
      </div>
      <div className="mt-3">
        <p className="text-sm text-gray-500">{label}</p>
        {loading ? (
          <div className="h-8 w-20 bg-gray-200 animate-pulse rounded mt-1" />
        ) : (
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        )}
        {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
      </div>
    </div>
  );
};

const CreditBadge = ({ credits }) => {
  if (!credits || credits === 0) return null;
  
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs font-medium border border-amber-200">
      <FiBarChart2 className="w-3 h-3" />
      {credits} {credits === 1 ? 'credit' : 'credits'}
    </span>
  );
};

const SubjectCard = ({ subject, showTarget = false, index }) => {
  const [expanded, setExpanded] = useState(false);
  const hasCredits = subject.source?.credits > 0 || subject.target?.credits > 0;
  
  return (
    <div className="border border-gray-100 rounded-lg mb-2 overflow-hidden hover:border-gray-200 transition-colors">
      <div 
        className="flex items-center justify-between p-3 bg-white cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 flex-1">
          <span className="text-xs text-gray-400 w-6">{index + 1}.</span>
          <div className={`w-2 h-2 rounded-full ${
            subject.status === 'matchable' ? 'bg-green-500' : 
            subject.status === 'unmatched' ? 'bg-amber-500' : 'bg-gray-400'
          }`} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{subject.source?.name || 'Unknown'}</span>
              <span className="text-xs text-gray-500 font-mono">({subject.source?.code || 'N/A'})</span>
              {hasCredits && <CreditBadge credits={subject.source?.credits} />}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <FiGrid className="w-3 h-3" />
                {subject.source?.departmentName || 'Unknown'}
              </span>
              <span>•</span>
              <span className="capitalize">{subject.source?.type || 'unknown'}</span>
              {subject.source?.isLab && <span className="text-indigo-600">• Lab</span>}
              {subject.source?.isElective && <span className="text-purple-600">• Elective</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {subject.status === 'matchable' ? (
            <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded-full">Matched</span>
          ) : (
            <span className="text-xs bg-amber-50 text-amber-600 px-2 py-1 rounded-full">No match</span>
          )}
          {expanded ? <FiChevronUp className="w-4 h-4 text-gray-400" /> : <FiChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>
      
      {expanded && (
        <div className="p-4 bg-gray-50 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-6">
            {/* Source Details */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Source</h5>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name:</span>
                  <span className="font-medium text-gray-900">{subject.source?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Code:</span>
                  <span className="font-mono text-gray-900">{subject.source?.code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Type:</span>
                  <span className="capitalize text-gray-900">
                    {subject.source?.type}
                    {subject.source?.isLab && ' (Lab)'}
                    {subject.source?.isElective && ' (Elective)'}
                  </span>
                </div>
                {subject.source?.credits > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Credits:</span>
                    <span className="font-medium text-amber-700">{subject.source.credits}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Mandatory:</span>
                  <span className={subject.source?.isMandatory ? 'text-green-600' : 'text-amber-600'}>
                    {subject.source?.isMandatory ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Target Details (if matched) */}
            {subject.target && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Target Match</h5>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Name:</span>
                    <span className="font-medium text-gray-900">{subject.target.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Code:</span>
                    <span className="font-mono text-gray-900">{subject.target.code}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Type:</span>
                    <span className="capitalize text-gray-900">
                      {subject.target.type}
                      {subject.target.isLab && ' (Lab)'}
                    </span>
                  </div>
                  {subject.target.credits > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Credits:</span>
                      <span className="font-medium text-amber-700">{subject.target.credits}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DepartmentSection = ({ department, showTarget = false, programHasCredits }) => {
  const [expanded, setExpanded] = useState(true);
  const deptHasCredits = department.subjects?.some(s => 
    (s.source?.credits > 0) || (s.target?.credits > 0)
  );
  
  return (
    <div className="border border-gray-100 rounded-lg mb-4 overflow-hidden bg-white">
      <div 
        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <FiGrid className="w-4 h-4 text-gray-500" />
          <span className="font-semibold text-gray-800">{department.departmentName}</span>
          {department.departmentCode && (
            <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full">
              {department.departmentCode}
            </span>
          )}
          {deptHasCredits && programHasCredits && (
            <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
              <FiBarChart2 className="w-3 h-3" />
              Credits
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span className={`text-xs px-2 py-1 rounded-full ${
            department.matchableSubjects === department.totalSubjects
              ? 'bg-green-50 text-green-700'
              : department.matchableSubjects === 0
              ? 'bg-red-50 text-red-700'
              : 'bg-amber-50 text-amber-700'
          }`}>
            {department.matchableSubjects || 0}/{department.totalSubjects || 0} matched
          </span>
          {expanded ? <FiChevronUp className="w-4 h-4 text-gray-400" /> : <FiChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>
      
      {expanded && (
        <div className="p-4">
          {department.subjects && department.subjects.map((subject, idx) => (
            <SubjectCard 
              key={idx} 
              subject={subject} 
              showTarget={showTarget} 
              index={idx}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProgramCard = ({ program, showTarget = false }) => {
  const [expanded, setExpanded] = useState(true);
  const programHasCredits = program.departments?.some(dept =>
    dept.subjects?.some(s => s.source?.credits > 0)
  );
  
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <div 
        className="bg-gradient-to-r from-gray-50 to-white px-5 py-4 border-b border-gray-100 cursor-pointer hover:from-gray-100 transition-all"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <FiBook className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900">{program.programName}</h4>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-500">Code: {program.programCode}</span>
                <span className="text-xs text-gray-400">•</span>
                <span className="text-xs text-gray-500">{program.programType}</span>
                {programHasCredits && (
                  <>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <FiBarChart2 className="w-3 h-3" />
                      Credit-based
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-sm font-medium text-gray-900">{program.matchableSubjects || 0}</span>
              <span className="text-sm text-gray-400">/</span>
              <span className="text-sm text-gray-600">{program.totalSubjects || 0}</span>
              <p className="text-xs text-gray-500">subjects</p>
            </div>
            {expanded ? <FiChevronUp className="w-5 h-5 text-gray-400" /> : <FiChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="p-5">
          {program.departments && program.departments.map((dept, idx) => (
            <DepartmentSection 
              key={idx} 
              department={dept} 
              showTarget={showTarget}
              programHasCredits={programHasCredits}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, children, isProcessing }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in-up">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        </div>
        <div className="p-6">
          {children}
        </div>
        <div className="p-6 bg-gray-50 flex justify-end gap-3 border-t border-gray-100">
          <button 
            onClick={onClose} 
            disabled={isProcessing}
            className="px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={onConfirm}
            disabled={isProcessing}
            className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm hover:shadow transition-all flex items-center gap-2"
          >
            {isProcessing ? (
              <>
                <FiLoader className="animate-spin w-4 h-4" />
                Processing...
              </>
            ) : (
              <>
                <FiCopy className="w-4 h-4" />
                Confirm & Copy
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---

const CopyProgramMapping = ({ tenantId, onComplete }) => {
  console.log('🚀 CopyProgramMapping mounted');

  // Steps
  const steps = ['Select Context', 'Review Preview', 'Complete'];
  const [currentStep, setCurrentStep] = useState(1);

  // Academic data hooks
  const sourceAcademicData = useAcademicData(tenantId, true);
  const targetAcademicData = useAcademicData(tenantId, false);

  // Source state
  const {
    academicYears: sourceAcademicYears,
    activeAcademicYears: sourceActiveYears,
    completedAcademicYears: sourceCompletedYears,
    loading: loadingSourceAY,
    loadingContexts: loadingSourceContexts,
    selectedAcademicYear: sourceSelectedYear,
    setSelectedAcademicYear: setSourceSelectedYear,
    selectedSemester: sourceSelectedSemester,
    setSelectedSemester: setSourceSelectedSemester,
    selectedTerm: sourceSelectedTerm,
    setSelectedTerm: setSourceSelectedTerm,
    semesters: sourceSemesters,
    terms: sourceTerms,
    isSemesterBased: sourceIsSemesterBased
  } = sourceAcademicData;

  // Target state
  const {
    academicYears: targetAcademicYears,
    activeAcademicYears: targetActiveYears,
    loading: loadingTargetAY,
    loadingContexts: loadingTargetContexts,
    selectedAcademicYear: targetSelectedYear,
    setSelectedAcademicYear: setTargetSelectedYear,
    selectedSemester: targetSelectedSemester,
    setSelectedSemester: setTargetSelectedSemester,
    selectedTerm: targetSelectedTerm,
    setSelectedTerm: setTargetSelectedTerm,
    semesters: targetSemesters,
    terms: targetTerms,
    isSemesterBased: targetIsSemesterBased
  } = targetAcademicData;

  // Local state
  const [sourceAcademicYear, setSourceAcademicYear] = useState('');
  const [targetAcademicYear, setTargetAcademicYear] = useState('');
  const [sourceSemester, setSourceSemester] = useState('');
  const [targetSemester, setTargetSemester] = useState('');
  const [sourceTerm, setSourceTerm] = useState('');
  const [targetTerm, setTargetTerm] = useState('');
  
  // Data states
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copyResults, setCopyResults] = useState(null);
  const [, setValidationData] = useState(null);
  const [validating, setValidating] = useState(false);
  const [modalState, setModalState] = useState({ isOpen: false, title: '', content: null });

  // Options
  const [options, setOptions] = useState({
    overwrite: false,
    preserveCredits: true,
    force: false
  });

  // Derived states
  const sourceAYDetails = sourceAcademicYears.find(ay => ay._id === sourceAcademicYear);
  const targetAYDetails = targetAcademicYears.find(ay => ay._id === targetAcademicYear);
  const systemMatch = sourceAYDetails?.isSemesterBased === targetAYDetails?.isSemesterBased;
  const hasCredits = previewData?.programs?.some(p => 
    p.departments?.some(d => 
      d.subjects?.some(s => s.source?.credits > 0)
    )
  );

  // Update local state from hooks
  useEffect(() => {
    setSourceAcademicYear(sourceSelectedYear);
  }, [sourceSelectedYear]);

  useEffect(() => {
    setTargetAcademicYear(targetSelectedYear);
  }, [targetSelectedYear]);

  useEffect(() => {
    setSourceSemester(sourceSelectedSemester);
  }, [sourceSelectedSemester]);

  useEffect(() => {
    setTargetSemester(targetSelectedSemester);
  }, [targetSelectedSemester]);

  useEffect(() => {
    setSourceTerm(sourceSelectedTerm);
  }, [sourceSelectedTerm]);

  useEffect(() => {
    setTargetTerm(targetSelectedTerm);
  }, [targetSelectedTerm]);

  // Check existing mappings
  const checkExistingMapping = useCallback(async () => {
    if (!targetAcademicYear) return false;
    
    try {
      const token = localStorage.getItem('token');
      const params = {
        tenantId,
        academicYearId: targetAcademicYear,
        ...(targetIsSemesterBased ? { semesterId: targetSemester } : { termId: targetTerm })
      };
      
      const response = await api.get('/api/program-subject-mappings', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params
      });
      
      return response.data.mappings?.length > 0;
    } catch {
      return false;
    }
  }, [tenantId, targetAcademicYear, targetSemester, targetTerm, targetIsSemesterBased]);

  // Validate departments
  const validateDepartments = useCallback(async () => {
    if (!sourceAcademicYear || !targetAcademicYear) return;
    
    try {
      setValidating(true);
      const token = localStorage.getItem('token');
      const response = await api.get('/api/program-subject-mappings/validate-departments', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: { tenantId, sourceAcademicYearId: sourceAcademicYear, targetAcademicYearId: targetAcademicYear }
      });
      
      if (response.data.success) {
        setValidationData(response.data.data);
      }
    } catch (error) {
      console.error('Error validating departments:', error);
    } finally {
      setValidating(false);
    }
  }, [tenantId, sourceAcademicYear, targetAcademicYear]);

  // Fetch preview
  const fetchPreview = useCallback(async () => {
    if (!sourceAcademicYear || !targetAcademicYear) {
      setPreviewError('Please select both academic years');
      return;
    }

    if (sourceAYDetails?.isSemesterBased && !sourceSemester) {
      setPreviewError('Please select source semester');
      return;
    }
    if (!sourceAYDetails?.isSemesterBased && !sourceTerm) {
      setPreviewError('Please select source term');
      return;
    }

    if (targetAYDetails?.isSemesterBased && !targetSemester) {
      setPreviewError('Please select target semester');
      return;
    }
    if (!targetAYDetails?.isSemesterBased && !targetTerm) {
      setPreviewError('Please select target term');
      return;
    }

    try {
      setLoadingPreview(true);
      setPreviewError('');
      setPreviewData(null);
      
      const token = localStorage.getItem('token');
      const params = {
        tenantId,
        sourceAcademicYearId: sourceAcademicYear,
        targetAcademicYearId: targetAcademicYear
      };

      if (sourceAYDetails?.isSemesterBased) {
        params.sourceSemesterId = sourceSemester;
      } else {
        params.sourceTermId = sourceTerm;
      }

      if (targetAYDetails?.isSemesterBased) {
        params.targetSemesterId = targetSemester;
      } else {
        params.targetTermId = targetTerm;
      }

      const response = await api.get('/api/program-subject-mappings/detailed-preview', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params
      });
      
      if (response.data.success) {
        setPreviewData(response.data.data);
        await validateDepartments();
        setCurrentStep(2);
      } else {
        setPreviewError(response.data.message || 'Failed to generate preview');
      }
    } catch (error) {
      setPreviewError(error.response?.data?.message || error.message || 'Failed to generate preview');
    } finally {
      setLoadingPreview(false);
    }
  }, [tenantId, sourceAcademicYear, targetAcademicYear, sourceSemester, targetSemester, sourceTerm, targetTerm, sourceAYDetails, targetAYDetails, validateDepartments]);

  // Execute copy
  const executeCopy = async () => {
    try {
      setCopying(true);
      
      const exists = await checkExistingMapping();
      if (exists && !options.overwrite) {
        const confirmOverwrite = window.confirm(
          'Mappings already exist in target. Overwrite?'
        );
        if (!confirmOverwrite) {
          setCopying(false);
          return;
        }
        options.overwrite = true;
      }
      
      const token = localStorage.getItem('token');
      const requestBody = {
        tenantId,
        sourceAcademicYearId: sourceAcademicYear,
        targetAcademicYearId: targetAcademicYear,
        options
      };

      if (sourceAYDetails?.isSemesterBased) {
        requestBody.sourceSemesterId = sourceSemester;
      } else {
        requestBody.sourceTermId = sourceTerm;
      }

      if (targetAYDetails?.isSemesterBased) {
        requestBody.targetSemesterId = targetSemester;
      } else {
        requestBody.targetTermId = targetTerm;
      }

      const response = await api.post('/api/program-subject-mappings/copy-ay', requestBody, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.data.success) {
        setCopyResults(response.data.data);
        toast.success('Copy completed successfully');
        setModalState(prev => ({ ...prev, isOpen: false }));
        setCurrentStep(3);
        if (onComplete) onComplete(response.data.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Copy failed');
    } finally {
      setCopying(false);
    }
  };

  const initiateCopy = () => {
    const content = (
      <div className="space-y-4">
        <div className="bg-indigo-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-2">Summary</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Source</p>
              <p className="font-medium">{previewData?.summary?.sourceAY?.name}</p>
              <p className="text-xs text-indigo-600">{previewData?.summary?.sourceContext?.name}</p>
            </div>
            <div>
              <p className="text-gray-500">Target</p>
              <p className="font-medium">{previewData?.summary?.targetAY?.name}</p>
              <p className="text-xs text-green-600">{previewData?.summary?.targetContext?.name}</p>
            </div>
          </div>
        </div>
        {previewData?.warnings?.length > 0 && (
          <div className="bg-amber-50 p-3 rounded-lg text-amber-700 text-sm">
            <FiAlertTriangle className="inline mr-1" />
            {previewData.warnings.length} warning(s)
          </div>
        )}
      </div>
    );

    setModalState({ isOpen: true, title: 'Confirm Copy', content });
  };

  const resetForm = () => {
    setSourceSelectedYear('');
    setTargetSelectedYear('');
    setPreviewData(null);
    setCopyResults(null);
    setCurrentStep(1);
  };

  const isLoading = loadingSourceAY || loadingTargetAY || loadingPreview || validating || loadingSourceContexts || loadingTargetContexts || copying;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FiCopy className="text-indigo-600" />
          Copy Program-Subject Mappings
        </h1>
        <p className="text-gray-500 mt-1">Transfer curriculum structures between academic years</p>
      </div>

      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} steps={steps} />

      {/* Step 1: Selection */}
      {currentStep === 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-2 gap-8">
            {/* Source */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-xs font-bold text-indigo-600">1</span>
                </div>
                <h3 className="font-semibold text-gray-800">Select Source</h3>
              </div>
              
              <select
                value={sourceAcademicYear}
                onChange={(e) => setSourceSelectedYear(e.target.value)}
                className="w-full p-3 border rounded-lg mb-3"
                disabled={loadingSourceAY}
              >
                <option value="">Choose academic year</option>
                {sourceActiveYears.map(ay => (
                  <option key={ay._id} value={ay._id}>
                    {ay.name} {ay.isSemesterBased ? '(Semester)' : '(Term)'}
                  </option>
                ))}
                {sourceCompletedYears.map(ay => (
                  <option key={ay._id} value={ay._id}>
                    {ay.name} {ay.isSemesterBased ? '(Semester)' : '(Term)'} (Completed)
                  </option>
                ))}
              </select>
              
              {sourceAYDetails && (
                <select
                  value={sourceIsSemesterBased ? sourceSemester : sourceTerm}
                  onChange={(e) => sourceIsSemesterBased 
                    ? setSourceSelectedSemester(e.target.value)
                    : setSourceSelectedTerm(e.target.value)
                  }
                  className="w-full p-3 border rounded-lg"
                  disabled={loadingSourceContexts}
                >
                  <option value="">Choose {sourceIsSemesterBased ? 'semester' : 'term'}</option>
                  {(sourceIsSemesterBased ? sourceSemesters : sourceTerms).map(item => (
                    <option key={item._id} value={item._id}>
                      {sourceIsSemesterBased 
                        ? (item.semesterName || `Semester ${item.semesterNumber}`)
                        : (item.termLabel || `Term ${item.termNumber}`)
                      }
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Target */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-xs font-bold text-green-600">2</span>
                </div>
                <h3 className="font-semibold text-gray-800">Select Target</h3>
              </div>
              
              <select
                value={targetAcademicYear}
                onChange={(e) => setTargetSelectedYear(e.target.value)}
                className="w-full p-3 border rounded-lg mb-3"
                disabled={loadingTargetAY}
              >
                <option value="">Choose academic year</option>
                {targetActiveYears
                  .filter(ay => ay._id !== sourceAcademicYear)
                  .map(ay => (
                    <option key={ay._id} value={ay._id}>
                      {ay.name} {ay.isSemesterBased ? '(Semester)' : '(Term)'}
                    </option>
                  ))}
              </select>
              
              {targetAYDetails && (
                <select
                  value={targetIsSemesterBased ? targetSemester : targetTerm}
                  onChange={(e) => targetIsSemesterBased
                    ? setTargetSelectedSemester(e.target.value)
                    : setTargetSelectedTerm(e.target.value)
                  }
                  className="w-full p-3 border rounded-lg"
                  disabled={loadingTargetContexts}
                >
                  <option value="">Choose {targetIsSemesterBased ? 'semester' : 'term'}</option>
                  {(targetIsSemesterBased ? targetSemesters : targetTerms).map(item => (
                    <option key={item._id} value={item._id}>
                      {targetIsSemesterBased
                        ? (item.semesterName || `Semester ${item.semesterNumber}`)
                        : (item.termLabel || `Term ${item.termNumber}`)
                      }
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* System Warning */}
          {sourceAYDetails && targetAYDetails && !systemMatch && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <FiAlertCircle className="inline mr-2 text-red-600" />
              <span className="text-sm text-red-700">
                System mismatch: Source is {sourceAYDetails.isSemesterBased ? 'Semester' : 'Term'}, 
                Target is {targetAYDetails.isSemesterBased ? 'Semester' : 'Term'}
              </span>
            </div>
          )}

          {/* Options */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Options</h4>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={options.overwrite}
                  onChange={(e) => setOptions(prev => ({ ...prev, overwrite: e.target.checked }))}
                  className="rounded text-indigo-600"
                />
                <span className="text-sm text-gray-600">Overwrite existing</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={options.preserveCredits}
                  onChange={(e) => setOptions(prev => ({ ...prev, preserveCredits: e.target.checked }))}
                  className="rounded text-indigo-600"
                />
                <span className="text-sm text-gray-600">Preserve credits</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={options.force}
                  onChange={(e) => setOptions(prev => ({ ...prev, force: e.target.checked }))}
                  className="rounded text-indigo-600"
                />
                <span className="text-sm text-gray-600">Force copy</span>
              </label>
            </div>
          </div>

          {/* Action */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={fetchPreview}
              disabled={isLoading || !sourceAcademicYear || !targetAcademicYear ||
                (sourceAYDetails?.isSemesterBased ? !sourceSemester : !sourceTerm) ||
                (targetAYDetails?.isSemesterBased ? !targetSemester : !targetTerm)}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loadingPreview ? <FiLoader className="animate-spin" /> : <FiEye />}
              {loadingPreview ? 'Generating...' : 'Preview Copy'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {currentStep === 2 && previewData && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <StatCard 
              label="Programs" 
              value={previewData.summary?.totalMappings || 0} 
              icon={FiLayers}
            />
            <StatCard 
              label="Subjects" 
              value={previewData.summary?.totalSubjects || 0} 
              icon={FiBook}
            />
            <StatCard 
              label="Matchable" 
              value={previewData.summary?.totalMatchable || 0} 
              icon={FiCheck}
              trend={previewData.summary?.coverage > 70 ? 'up' : 'down'}
            />
            <StatCard 
              label="Coverage" 
              value={`${previewData.summary?.coverage || 0}%`} 
              icon={FiPercent}
            />
          </div>

          {/* Context */}
          <div className="bg-gradient-to-r from-indigo-50 to-green-50 p-4 rounded-xl flex items-center justify-between">
            <div className="text-center flex-1">
              <p className="text-xs text-gray-500 mb-1">SOURCE</p>
              <p className="font-semibold">{previewData.summary?.sourceAY?.name}</p>
              <p className="text-sm text-indigo-600 mt-1">{previewData.summary?.sourceContext?.name}</p>
            </div>
            <FiArrowRight className="text-gray-400 mx-4" />
            <div className="text-center flex-1">
              <p className="text-xs text-gray-500 mb-1">TARGET</p>
              <p className="font-semibold">{previewData.summary?.targetAY?.name}</p>
              <p className="text-sm text-green-600 mt-1">{previewData.summary?.targetContext?.name}</p>
            </div>
          </div>

          {/* Warnings */}
          {previewData.warnings?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-amber-800 font-medium mb-2">
                <FiAlertTriangle className="w-4 h-4" />
                <span>Warnings</span>
              </div>
              <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
                {previewData.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* Programs */}
          <div className="space-y-4">
            {previewData.programs?.map((program, idx) => (
              <ProgramCard key={idx} program={program} showTarget={true} />
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            <button
              onClick={() => setCurrentStep(1)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Back
            </button>
            <div className="flex gap-3">
              <button
                onClick={fetchPreview}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <FiRefreshCw className={loadingPreview ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button
                onClick={initiateCopy}
                disabled={copying || (previewData.summary?.totalMatchable || 0) === 0}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {copying ? <FiLoader className="animate-spin" /> : <FiCopy />}
                {copying ? 'Copying...' : 'Copy Mappings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {currentStep === 3 && copyResults && (
        <div className="bg-white rounded-xl shadow-sm border border-green-200 p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Copy Completed</h3>
            <p className="text-gray-500 mt-1">
              {copyResults.summary?.createdMappings || 0} created, {copyResults.summary?.updatedMappings || 0} updated
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard 
              label="Subjects Copied" 
              value={copyResults.summary?.subjectsCopied || 0} 
              icon={FiBook}
            />
            <StatCard 
              label="Subjects Skipped" 
              value={copyResults.summary?.subjectsSkipped || 0} 
              icon={FiAlertTriangle}
            />
            <StatCard 
              label="Total Mappings" 
              value={copyResults.summary?.totalMappings || 0} 
              icon={FiLayers}
            />
          </div>

          {/* Detailed Results (only show if credits exist) */}
          {hasCredits && copyResults.details?.length > 0 && (
            <div className="mb-6">
              <h4 className="font-medium mb-3">Copied Subjects</h4>
              {copyResults.details.map((detail, idx) => (
                <div key={idx} className="border rounded-lg mb-4 p-4">
                  <div className="flex justify-between mb-3">
                    <span className="font-semibold">{detail.program?.name}</span>
                    <span className="text-sm text-gray-500">{detail.academicYear?.name}</span>
                  </div>
                  {detail.departments?.map((dept, dIdx) => (
                    <div key={dIdx} className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">{dept.departmentName}</p>
                      <div className="space-y-2">
                        {dept.subjects?.map((subj, sIdx) => (
                          <div key={sIdx} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                            <div className="flex items-center gap-2">
                              <span>{subj.name}</span>
                              <span className="text-gray-500 text-xs">({subj.code})</span>
                            </div>
                            {subj.credits > 0 && <CreditBadge credits={subj.credits} />}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-center gap-4 pt-4 border-t">
            <button
              onClick={resetForm}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Copy Another
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {previewError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-center gap-2">
          <FiAlertCircle className="w-5 h-5" />
          {previewError}
        </div>
      )}

      {/* Loading Overlay */}
      {(loadingPreview || validating) && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-40">
          <div className="text-center">
            <FiLoader className="animate-spin w-8 h-8 text-indigo-600 mx-auto mb-3" />
            <p className="text-gray-600">Analyzing curriculum...</p>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={modalState.isOpen}
        title={modalState.title}
        onClose={() => setModalState({ isOpen: false })}
        onConfirm={executeCopy}
        isProcessing={copying}
      >
        {modalState.content}
      </ConfirmationModal>
    </div>
  );
};

export default CopyProgramMapping;